import { Action } from "@reduxjs/toolkit";
import { Literal, Static, Union } from "runtypes";

import { IEnvironment } from "../utils/environment";

export const BuildSourceSystemType = Union(Literal("CloudTest"), Literal("Focus"), Literal("Official"));
export type IBuildSourceSystem = Static<typeof BuildSourceSystemType>;

export const EnvironmentType = Union(Literal("Cloud"), Literal("Classic"));
export type IEnvironmentType = Static<typeof EnvironmentType>;

export const Flavor = Union(Literal("debug"), Literal("retail"));
export type IFlavor = Static<typeof Flavor>;

export const BuildType = Union(Literal("CloudTest"), Literal("Focus"), Literal("Official"));
export type IBuildType = Static<typeof BuildType>;

export type ReplaceableType =
  | "StandardReplaceable"
  | "FlavorReplaceable"
  | "BuildSourceSystemReplaceable"
  | "BuildNumberReplaceable";

// Begin introducing types for the project. At this time, we will keep them in one file
export type IClient = {
  Name: string;
  Tasks: ITask[];
};

/**
 * The TDS service status codes
 */
export enum TdsServiceStatus {
  Normal = 0,
  Warning = 1,
  Error = 2,
}

/**
 * The state of the task
 */
export enum TaskState {
  Pending,
  Passed,
  Failed,
  Skipped,
  Running,
  Canceled,
  Paused,
}

export type ITask = {
  EndTime: string;
  Fatal: boolean;
  ID: number;
  Name: string;
  Pause: boolean;
  Skip: boolean;
  StartTime: string;
  State: TaskState;
};

export type ITaskLog = {
  Data: string;
  Description: string;
  Exception: string | null;
  ID: number;
  Level: number;
  Timestamp: string;
};

export type ITopologyDetails = {
  CreationTime: string;
  Description: string;
  ID: number;
  Name: string;
  State: TopologyState;
  Clients: IClient[];
};

export type IMachine = {
  BuildNumber: string;
  Fqdn: string;
  IPAddress: string;
  Name: string;
  OperatingSystem: string;
  RdpPort: string;
  TopologyRole: string;
  WrmPort: string;
  HttpsPort: string;
  VpnProfileName: string;
};

// Why... why did this happen? Why!
export type IMachineExt = IMachine & {
  VmAdmin: string;
};

export type Regions = {
  DisplayName: string;
  VpnProfileName: string;
  Id: number | null;
};

export type TopologyManagement = {
  Id: number;
  TopologyTypeId: number;
  Enabled: boolean;
  Regions: Regions;
};

export type RegionTrackingData = {
  SelectedRegion: string;
  AutoDetectedRegion?: string;
  GraphAPIMappedRegion?: string;
  ManualRegionSelectionReason?: string;
  ManualRegionSelectionJustification?: string;
}

export type TopologyType = {
  CheckInLookupFileName: string;
  CheckOutLookupFileName: string;
  DeploymentLookupFileName: string;
  DisplayName: string;
  Id: number;
  PartnerTeamId: number;
  PartnerTeamName: string;
  QuotaCost: number;
  TemplateOwnerName: string;
  environment: IEnvironment;
  TopologyManagements: TopologyManagement[];
  RequestRegionId: number | null;
  RegionTrackingData?: RegionTrackingData;
};

export type ColorIconType = {
  color: string;
  iconName: string;
};

export enum TopologyState {
  Pending,
  Ready,
  Failed,
  Paused,
  Queued
}

export enum QueueState {
  InProgress,
  Completed,
  Failed,
  Expired
}

/**
 * RecoverTime: the time ....
 * WarningTime: the time....
 */
export type ITopology = {
  CreatedTime: Date;
  Machines: IMachine[];
  MachinesExt: IMachineExt[];
  Name: string;
  Owner: string;
  PolicyID: number | null;
  RecoverTime: Date;
  Selected: boolean;
  TopologyId: number | undefined;
  TopologyState: TopologyState;
  TopologyType: TopologyType;
  WarningTime: string;
  WorkgroupId: number;
  environment: IEnvironment;
  Regions: Regions;
  QueuePosition: number;
  QueueState: QueueState | null;
};

export type IConnectionTypes = "TDSVPN" | "RDP" | "CORPNET";

export type OS = "Windows 2012R2" | "Windows Threshold" | "Windows 2019 Core" | "Windows Evergreen";

export type ErrorMessageAction = Action<string> & {
  error: string;
};

export type Replaceable = {
  Description: string | null;
  Name: string;
  Value: string | null;
};

export type IBlessedTemplate = {
  FilePath: string;
  Replaceables: Replaceable[];
  Type: TopologyType;
};

export type TaskActionTypes = "reset" | "skip" | "pause" | "resume";

type VerbTenseOptions = {
  Active: string;
  Past: string;
  Present: string;
};

export type TaskActionVerbMapping = {
  [Action in TaskActionTypes]: VerbTenseOptions;
};
